import { Snackbar, Alert, type AlertColor } from '@mui/material';
import { createContext, useContext, useState, type ReactNode, useCallback } from 'react';
import { colors, radius, fonts, shadows, typography, zIndex } from '@/theme/tokens';

interface ToastOptions {
  message: string;
  severity?: AlertColor;
  duration?: number;
}

interface ToastContextValue {
  showToast: (options: ToastOptions) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components -- context hook must be co-located with provider
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within AppToastProvider');
  }
  return context;
}

interface AppToastProviderProps {
  children: ReactNode;
}

// Shared color mapping used by both provider + standalone component
const severityColors: Record<
  AlertColor,
  { bg: string; text: string; icon: string; border: string }
> = {
  success: {
    bg: colors.accentLight,
    text: colors.textPrimary,
    icon: colors.success,
    border: colors.success,
  },
  error: {
    bg: '#FCEAEA',
    text: colors.textPrimary,
    icon: colors.danger,
    border: colors.danger,
  },
  warning: {
    bg: '#FDF5E0',
    text: colors.textPrimary,
    icon: colors.warning,
    border: colors.warning,
  },
  info: {
    bg: colors.accentLighter,
    text: colors.textPrimary,
    icon: colors.accent,
    border: colors.accent,
  },
};

const alertSx = (severity: AlertColor) => {
  const c = severityColors[severity];
  return {
    backgroundColor: c.bg,
    color: c.text,
    border: `1px solid ${c.border}`,
    borderRadius: radius.md,
    fontFamily: fonts.body,
    fontSize: typography.body.fontSize,
    fontWeight: typography.bodyMedium.fontWeight,
    boxShadow: shadows.md,
    '& .MuiAlert-icon': {
      color: c.icon,
    },
    '& .MuiAlert-action': {
      '& .MuiIconButton-root': {
        color: colors.textSecondary,
      },
    },
  };
};

export function AppToastProvider({ children }: AppToastProviderProps) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ToastOptions>({
    message: '',
    severity: 'info',
    duration: 4000,
  });

  const showToast = useCallback((opts: ToastOptions) => {
    setOptions({
      message: opts.message,
      severity: opts.severity || 'info',
      duration: opts.duration || 4000,
    });
    setOpen(true);
  }, []);

  const showSuccess = useCallback(
    (message: string) => showToast({ message, severity: 'success' }),
    [showToast]
  );

  const showError = useCallback(
    (message: string) => showToast({ message, severity: 'error' }),
    [showToast]
  );

  const showWarning = useCallback(
    (message: string) => showToast({ message, severity: 'warning' }),
    [showToast]
  );

  const showInfo = useCallback(
    (message: string) => showToast({ message, severity: 'info' }),
    [showToast]
  );

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  return (
    <ToastContext.Provider value={{ showToast, showSuccess, showError, showWarning, showInfo }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={options.duration}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{ zIndex: zIndex.toast }}
      >
        <Alert
          onClose={handleClose}
          severity={options.severity}
          variant="standard"
          sx={alertSx(options.severity || 'info')}
        >
          {options.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}

// Standalone toast component for simple use cases
interface AppToastProps {
  open: boolean;
  onClose: () => void;
  message: string;
  severity?: AlertColor;
  duration?: number;
}

export function AppToast({
  open,
  onClose,
  message,
  severity = 'info',
  duration = 4000,
}: AppToastProps) {
  return (
    <Snackbar
      open={open}
      autoHideDuration={duration}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      sx={{ zIndex: zIndex.toast }}
    >
      <Alert onClose={onClose} severity={severity} variant="standard" sx={alertSx(severity)}>
        {message}
      </Alert>
    </Snackbar>
  );
}
